/**
 * `/settings/sessions` — every live session, with per-row sign-out and
 * "Sign out everywhere else". The file is named `settings_.sessions` so this
 * route stays out of `/settings`'s tree (no Outlet). The confirm is a
 * module-bus promise, not URL state. It also links to
 * /settings/sign-in-methods, because the account screen's "Sign-in methods
 * and sessions" row lands here (ISSUE-1025).
 */
import {
  useSessionList,
  useSessionRevokeAllOthers,
  useSessionRevokeOne,
} from "@sportsmet/auth-sdk/private";
import { Button, confirmDestructive } from "@sportsmet/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { type ReactNode, useEffect, useState } from "react";

import { requireSignedIn } from "../auth/requireSignedIn";
import {
  extractErrorCode,
  showApiError,
  showOfflineError,
} from "../errors/apiErrorBus";
import { SettingsBackLink } from "../features/account/SettingsBackLink";
import { SessionList } from "../features/sessions/SessionList";
import {
  canSignOutEverywhereElse,
  SESSIONS_HEADING,
  SIGN_OUT_EVERYWHERE_CONFIRM_BODY,
  SIGN_OUT_EVERYWHERE_CONFIRM_TITLE,
  SIGN_OUT_EVERYWHERE_LABEL,
  SIGN_OUT_ROW_CONFIRM_BODY,
  SIGN_OUT_ROW_CONFIRM_LABEL,
  SIGN_OUT_ROW_LABEL,
  signOutRowConfirmTitle,
  sortSessionsForDisplay,
  type typeSessionRow,
} from "../features/sessions/sessionsScreenLogic";
import { SIGN_IN_METHODS_HEADING } from "../features/signInMethods/signInMethodsScreenLogic";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";
import { SectionScreen } from "../shell/SectionScreen";

const SessionsScreen = (): ReactNode => {
  const list = useSessionList({});
  const revokeOne = useSessionRevokeOne();
  const revokeAllOthers = useSessionRevokeAllOthers();
  // One clock read per mount. "2h ago" does not need to tick.
  const [nowMs] = useState(() => Date.now());

  useEffect(() => {
    const code = extractErrorCode(list.data);
    if (code !== null) showApiError(list.data, undefined, list.dataUpdatedAt);
  }, [list.dataUpdatedAt]);

  if (list.isPending) return <RoutePendingScreen />;

  const sessions: typeSessionRow[] = sortSessionsForDisplay(
    list.data?.data?.items ?? [],
  );
  const signingOutSessionId =
    revokeOne.isPending && revokeOne.variables.sessionId
      ? revokeOne.variables.sessionId
      : null;

  async function handleSignOutRow(session: typeSessionRow): Promise<void> {
    const confirmed = await confirmDestructive({
      title: signOutRowConfirmTitle(session.deviceLabel),
      body: SIGN_OUT_ROW_CONFIRM_BODY,
      confirmLabel: SIGN_OUT_ROW_CONFIRM_LABEL,
    });
    if (!confirmed) return;
    try {
      showApiError(
        await revokeOne.mutateAsync({ sessionId: session.sessionId }),
      );
    } catch {
      showOfflineError();
    }
  }

  async function handleSignOutEverywhereElse(): Promise<void> {
    const confirmed = await confirmDestructive({
      title: SIGN_OUT_EVERYWHERE_CONFIRM_TITLE,
      body: SIGN_OUT_EVERYWHERE_CONFIRM_BODY,
      confirmLabel: SIGN_OUT_EVERYWHERE_LABEL,
    });
    if (!confirmed) return;
    try {
      showApiError(await revokeAllOthers.mutateAsync({}));
    } catch {
      showOfflineError();
    }
  }

  return (
    <SectionScreen heading={SESSIONS_HEADING} back={<SettingsBackLink />}>
      <Link
        to="/settings/sign-in-methods"
        data-testid="sessions-sign-in-methods-link"
        className="flex min-h-touch-target items-center text-body text-primary underline underline-offset-4"
      >
        {SIGN_IN_METHODS_HEADING}
      </Link>
      <SessionList
        sessions={sessions}
        nowMs={nowMs}
        signOutLabel={SIGN_OUT_ROW_LABEL}
        signingOutSessionId={signingOutSessionId}
        onSignOut={(session) => {
          void handleSignOutRow(session);
        }}
      />
      <Button
        variant="destructive"
        fullWidth
        className="md:w-auto md:self-start"
        disabled={!canSignOutEverywhereElse(sessions)}
        loading={revokeAllOthers.isPending}
        loadingLabel="Signing out every other session"
        onClick={() => {
          void handleSignOutEverywhereElse();
        }}
      >
        {SIGN_OUT_EVERYWHERE_LABEL}
      </Button>
    </SectionScreen>
  );
};

export const Route = createFileRoute("/settings_/sessions")({
  beforeLoad: requireSignedIn,
  component: SessionsScreen,
});
